import React, { useState } from "react";
import { Plus, Trash, DownloadSimple, Folder } from "@phosphor-icons/react";
import { jobs, fmt, formatDMS } from "../lib/api";
import { toast } from "sonner";

export const PointList = ({ job, points, state, onCapture, onDelete, onNewJob }) => {
  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const capture = async () => {
    if (!job) {
      toast.error("Create a job first");
      return;
    }
    if (!state?.connected) {
      toast.error("Station is offline");
      return;
    }
    setSaving(true);
    try {
      const p = {
        code: code || null,
        description: desc || null,
        hz: state.hz,
        v: state.v,
        sd: state.sd,
        x: state.target_x,
        y: state.target_y,
        z: state.target_z,
      };
      await onCapture(p);
      setCode("");
      setDesc("");
      toast.success(`Point #${points.length + 1} saved`);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel flex h-full flex-col" data-testid="point-list-panel">
      <div className="panel-header">
        <span className="flex items-center gap-2">
          <Folder size={14} weight="bold" />
          {job ? job.name : "NO JOB"}
        </span>
        <div className="flex items-center gap-2">
          <button
            data-testid="new-job-btn"
            onClick={onNewJob}
            className="rounded-sm border border-[var(--border)] px-2 py-1 text-[10px] uppercase tracking-widest text-[var(--text-dim)] hover:border-[var(--neon)] hover:text-[var(--neon)]"
          >
            New Job
          </button>
          {job && (
            <a
              data-testid="export-csv-btn"
              href={jobs.csvUrl(job.id)}
              className="flex items-center gap-1 rounded-sm border border-[var(--border)] px-2 py-1 text-[10px] uppercase tracking-widest text-[var(--text-dim)] hover:border-[var(--neon)] hover:text-[var(--neon)]"
            >
              <DownloadSimple size={12} weight="bold" /> CSV
            </a>
          )}
        </div>
      </div>

      {/* Capture bar */}
      <div className="grid grid-cols-[1fr,2fr,auto] gap-2 border-b border-[var(--border)] p-3">
        <input
          data-testid="point-code-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Code"
          className="readout rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
        />
        <input
          data-testid="point-desc-input"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Description"
          className="rounded-sm border border-[var(--border-strong)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--neon)]"
        />
        <button
          data-testid="capture-point-btn"
          onClick={capture}
          disabled={saving || !job}
          className="btn-tac !min-h-[40px] px-4"
          data-variant="primary"
        >
          <span className="flex items-center gap-1">
            <Plus size={16} weight="bold" /> Save
          </span>
        </button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-auto">
        {points.length === 0 ? (
          <div className="p-6 text-center text-sm text-[var(--text-mute)]">
            {job ? "No points captured yet." : "Create a job to start capturing points."}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-[var(--surface-2)] text-[var(--text-dim)]">
              <tr>
                <Th>#</Th>
                <Th>Code</Th>
                <Th>Hz</Th>
                <Th>V</Th>
                <Th>SD</Th>
                <Th>X</Th>
                <Th>Y</Th>
                <Th>Z</Th>
                <Th className="w-8" />
              </tr>
            </thead>
            <tbody className="readout tabular-nums">
              {points.map((p) => (
                <tr key={p.id} className="border-t border-[var(--border)] hover:bg-[var(--surface-2)]">
                  <Td className="text-[var(--neon)]">{p.number}</Td>
                  <Td>{p.code || "—"}</Td>
                  <Td>{formatDMS(p.hz)}</Td>
                  <Td>{formatDMS(p.v)}</Td>
                  <Td>{fmt(p.sd, 3)}</Td>
                  <Td>{fmt(p.x, 3)}</Td>
                  <Td>{fmt(p.y, 3)}</Td>
                  <Td>{fmt(p.z, 3)}</Td>
                  <Td>
                    <button
                      data-testid={`delete-point-${p.number}`}
                      onClick={() => onDelete(p.id)}
                      className="text-[var(--text-mute)] hover:text-[var(--danger)]"
                      aria-label="Delete point"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`px-2 py-2 text-left text-[10px] uppercase tracking-widest font-normal ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-2 py-1.5 ${className}`}>{children}</td>
);
